import { IProxy } from "@interfaces/admin/proxy";
import { EOperator } from "@interfaces/operator.enum";
import { createSlice } from "@reduxjs/toolkit";
import { SliceAction } from "store/store";

const queryParamsLS = JSON.parse(window.localStorage.getItem("adminProxyPage") || "{}");
const queryParamsURL = Object.fromEntries(new URLSearchParams(window.location.search));

const isActivePage = window.location.pathname === (process.env.NODE_ENV === "production" ? "/" : "/admin");

const initialState: {
    activePaginationPage: number,
    activePaginationLimit: number,
    activeSortParameter: string,
    activeSearchIdParameter: string,
    activeSearchPhoneParameter: string,
    activeOperator: string,
    activeSelling: string,
    rowsIsError: Record<string, string[]>
} = Object.assign(
    (isActivePage && Object.keys(queryParamsURL).length)
        ? {
            activePaginationPage: Number(queryParamsURL?.page || "1"),
            activePaginationLimit: Number(queryParamsURL?.limit || "50"),
            activeSortParameter: queryParamsURL?.sort || "",
            activeSearchIdParameter: queryParamsURL?.searchId || "",
            activeSearchPhoneParameter: queryParamsURL?.searchPhone || "",
            activeOperator: queryParamsURL.operator || "",
            activeSelling: queryParamsURL.selling || "",
        }
        : {
            activePaginationPage: Number(queryParamsLS?.page || "1"),
            activePaginationLimit: Number(queryParamsLS?.limit || "50"),
            activeSortParameter: queryParamsLS?.sort || "",
            activeSearchIdParameter: queryParamsLS?.searchId || "",
            activeSearchPhoneParameter: queryParamsLS?.searchPhone || "",
            activeOperator: queryParamsLS.operator || "",
            activeSelling: queryParamsLS.selling || "",
        }, {
    rowsIsError: {}
});

window.localStorage.setItem("adminProxyPage", JSON.stringify({
    page: initialState.activePaginationPage,
    limit: initialState.activePaginationLimit,
    sort: initialState.activeSortParameter,
    searchId: initialState.activeSearchIdParameter,
    searchPhone: initialState.activeSearchPhoneParameter,
    operator: initialState.activeOperator,
    selling: initialState.activeSelling
}))

const myProxiesSlice = createSlice({
    initialState,
    name: "myProxiesAdminPage",
    reducers: {
        setActivePaginationPage(state, action: SliceAction<(typeof initialState)["activePaginationPage"]>) {
            window.localStorage.setItem("adminProxyPage", JSON.stringify({
                page: action.payload,
                limit: state.activePaginationLimit,
                sort: state.activeSortParameter,
                searchId: state.activeSearchIdParameter,
                searchPhone: state.activeSearchPhoneParameter,
                operator: state.activeOperator,
                selling: state.activeSelling
            }))
            state.activePaginationPage = action.payload;
        },
        setActivePaginationLimit(state, action: SliceAction<(typeof initialState)["activePaginationLimit"]>) {
            window.localStorage.setItem("adminProxyPage", JSON.stringify({
                page: state.activePaginationPage,
                limit: action.payload,
                sort: state.activeSortParameter,
                searchId: state.activeSearchIdParameter,
                searchPhone: state.activeSearchPhoneParameter,
                operator: state.activeOperator,
                selling: state.activeSelling
            }))
            state.activePaginationLimit = action.payload;
        },
        setActiveSortParameter(state, action: SliceAction<(typeof initialState)["activeSortParameter"]>) {
            window.localStorage.setItem("adminProxyPage", JSON.stringify({
                page: state.activePaginationPage,
                limit: state.activePaginationLimit,
                sort: action.payload,
                searchId: state.activeSearchIdParameter,
                searchPhone: state.activeSearchPhoneParameter,
                operator: state.activeOperator,
                selling: state.activeSelling
            }))
            state.activeSortParameter = action.payload;
        },
        setActiveSearchIdParameter(state, action: SliceAction<(typeof initialState)["activeSearchIdParameter"]>) {
            window.localStorage.setItem("adminProxyPage", JSON.stringify({
                page: state.activePaginationPage,
                limit: state.activePaginationLimit,
                sort: state.activeSortParameter,
                searchId: action.payload,
                searchPhone: state.activeSearchPhoneParameter,
                operator: state.activeOperator,
                selling: state.activeSelling
            }))
            state.activeSearchIdParameter = action.payload;
        },
        setActiveSearchPhoneParameter(state, action: SliceAction<(typeof initialState)["activeSearchPhoneParameter"]>) {
            window.localStorage.setItem("adminProxyPage", JSON.stringify({
                page: state.activePaginationPage,
                limit: state.activePaginationLimit,
                sort: state.activeSortParameter,
                searchId: state.activeSearchIdParameter,
                searchPhone: action.payload,
                operator: state.activeOperator,
                selling: state.activeSelling
            }))
            state.activeSearchPhoneParameter = action.payload;
        },
        setActiveOperator(state, action: SliceAction<(typeof initialState)["activeOperator"]>) {
            window.localStorage.setItem("adminProxyPage", JSON.stringify({
                page: state.activePaginationPage,
                limit: state.activePaginationLimit,
                sort: state.activeSortParameter,
                searchId: state.activeSearchIdParameter,
                searchPhone: state.activeSearchPhoneParameter,
                operator: action.payload,
                selling: state.activeSelling
            }))
            state.activeOperator = action.payload;
        },
        setActiveSelling(state, action: SliceAction<(typeof initialState)["activeSelling"]>) {
            window.localStorage.setItem("adminProxyPage", JSON.stringify({
                page: state.activePaginationPage,
                limit: state.activePaginationLimit,
                sort: state.activeSortParameter,
                searchId: state.activeSearchIdParameter,
                searchPhone: state.activeSearchPhoneParameter,
                operator: state.activeOperator,
                selling: action.payload
            }))
            state.activeSelling = action.payload;
        },

        addRowError(state, action: SliceAction<{
            proxyId: IProxy["id"],
            colName: string
        }>) {
            const { proxyId, colName } = action.payload

            const listValues = state.rowsIsError?.[proxyId] || []

            if (!listValues.includes(colName)) {
                state.rowsIsError[proxyId] = [...listValues, colName];
            }
        },
        removeRowError(state, action: SliceAction<{
            proxyId: IProxy["id"],
            colName: string
        }>) {
            const { proxyId, colName } = action.payload;

            const listValues = state.rowsIsError?.[proxyId] || []

            state.rowsIsError[proxyId] = listValues.filter(item => item !== colName)
        }
    },
});

const { reducer, actions } = myProxiesSlice;

export default reducer;

export const {
    setActivePaginationPage,
    setActivePaginationLimit,
    setActiveSortParameter,
    setActiveSearchIdParameter,
    setActiveSearchPhoneParameter,
    setActiveOperator,
    setActiveSelling,
    addRowError,
    removeRowError
} = actions;
